/**
 * Hook para estatísticas do Dashboard Admin
 * Utiliza RPCs para agregação no banco, contornando limite de 1000 registros do Supabase
 * 
 * @version 1.1.0
 * @date 2026-03-29 - Migrado inboxes/providers para RPC server-side
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// =====================================================
// Interfaces
// =====================================================

interface TenantStats {
  total: number;
  active: number;
  trial: number;
  suspended: number;
  cancelled: number;
}

interface MessageStats {
  total: number;
  today: number;
  thisWeek: number;
  thisMonth: number;
}

interface TopTenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  messageCount: number;
  conversationCount: number;
  agentCount: number;
}

interface PlanDistribution {
  plan: string;
  count: number;
  color: string;
}

interface ProviderHealth {
  providerId: string;
  providerName: string;
  providerSlug: string;
  logoUrl: string | null;
  channelType: string;
  totalInboxes: number;
  activeInboxes: number;
  connectingInboxes: number;
  errorInboxes: number;
  disconnectedInboxes: number;
  inactiveInboxes: number;
  healthScore: number;
}

interface SystemAlert {
  id: string;
  type: 'error' | 'warning' | 'info';
  title: string;
  description: string;
  tenantName?: string;
  inboxName?: string;
  createdAt: string;
}

interface AdminDashboardStats {
  tenants: TenantStats;
  messages: MessageStats;
  totalUsers: number;
  totalInboxes: number;
  topTenants: TopTenant[];
  planDistribution: PlanDistribution[];
  providerHealth: ProviderHealth[];
  systemAlerts: SystemAlert[];
}

// Interface para retorno da RPC get_admin_inboxes_summary
interface AdminInboxRow {
  inbox_id: string;
  inbox_name: string;
  inbox_status: string;
  inbox_channel_type: string;
  tenant_name: string;
  provider_name: string | null;
  provider_display_name: string | null;
  provider_slug: string | null;
  provider_logo_url: string | null;
  provider_channel_type: string | null;
  provider_is_active: boolean | null;
}

// =====================================================
// Constantes
// =====================================================

const PLAN_COLORS: Record<string, string> = {
  free: 'hsl(var(--muted-foreground))',
  starter: 'hsl(var(--chart-1))',
  professional: 'hsl(var(--chart-2))',
  enterprise: 'hsl(var(--chart-3))',
};

// =====================================================
// Hook Principal
// =====================================================

export function useAdminDashboardStats() {
  return useQuery({
    queryKey: ['admin-dashboard-stats'],
    queryFn: async (): Promise<AdminDashboardStats> => {
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay()).toISOString();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      // Buscar dados básicos e RPCs em paralelo
      const [
        tenantsResult,
        profilesResult,
        // v1.1.0: Usa RPC server-side para inboxes (sem expor provider_id/tenant_id)
        adminInboxesResult,
        tenantMembersResult,
        // RPCs para contagem precisa de mensagens e conversas
        conversationStatsResult,
        messageStatsResult,
        messagesTodayResult,
        messagesWeekResult,
        messagesMonthResult,
      ] = await Promise.all([
        // Dados básicos
        supabase.from('tenants').select('id, name, slug, plan, status'),
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        // v1.1.0: RPC segura que retorna inboxes + providers sem expor UUIDs internos
        supabase.rpc('get_admin_inboxes_summary'),
        supabase.from('tenant_members').select('tenant_id'),
        // RPCs para agregação precisa
        supabase.rpc('get_admin_conversation_stats_by_tenant'),
        supabase.rpc('get_admin_messages_count_by_tenant'),
        supabase.from('messages').select('id', { count: 'exact', head: true }).gte('sent_at', startOfDay),
        supabase.from('messages').select('id', { count: 'exact', head: true }).gte('sent_at', startOfWeek),
        supabase.from('messages').select('id', { count: 'exact', head: true }).gte('sent_at', startOfMonth),
      ]);

      const tenants = tenantsResult.data || [];
      // v1.1.0: Dados de inboxes vêm da RPC (formato flat)
      const adminInboxes: AdminInboxRow[] = (adminInboxesResult.data || []) as AdminInboxRow[];
      const tenantMembers = tenantMembersResult.data || [];
      const conversationStats = conversationStatsResult.data || [];
      const messageStats = messageStatsResult.data || [];

      // Mapear conversas por tenant (via RPC)
      const conversationsPerTenant = new Map<string, number>();
      conversationStats.forEach((stat: { tenant_id: string; conversation_count: number }) => {
        conversationsPerTenant.set(stat.tenant_id, Number(stat.conversation_count));
      });

      // Mapear mensagens por tenant (via RPC)
      const messagesPerTenant = new Map<string, number>();
      let totalMessages = 0;
      messageStats.forEach((stat: { tenant_id: string; message_count: number }) => {
        messagesPerTenant.set(stat.tenant_id, Number(stat.message_count));
        totalMessages += Number(stat.message_count);
      });

      // Calcular estatísticas de tenant
      const tenantStatsData: TenantStats = {
        total: tenants.length,
        active: tenants.filter(t => t.status === 'active').length,
        trial: tenants.filter(t => t.status === 'trial').length,
        suspended: tenants.filter(t => t.status === 'suspended').length,
        cancelled: tenants.filter(t => t.status === 'cancelled').length,
      };

      // Calcular estatísticas de mensagens (total via RPC, períodos via count)
      const messageStatsData: MessageStats = {
        total: totalMessages,
        today: messagesTodayResult.count || 0,
        thisWeek: messagesWeekResult.count || 0,
        thisMonth: messagesMonthResult.count || 0,
      };

      // Contar agentes por tenant
      const agentsPerTenant = new Map<string, number>();
      tenantMembers.forEach((member: { tenant_id: string }) => {
        agentsPerTenant.set(member.tenant_id, (agentsPerTenant.get(member.tenant_id) || 0) + 1);
      });

      // Construir lista de top tenants usando dados agregados das RPCs
      const topTenants: TopTenant[] = tenants
        .map(tenant => ({
          id: tenant.id,
          name: tenant.name,
          slug: tenant.slug,
          plan: tenant.plan,
          status: tenant.status,
          messageCount: messagesPerTenant.get(tenant.id) || 0,
          conversationCount: conversationsPerTenant.get(tenant.id) || 0,
          agentCount: agentsPerTenant.get(tenant.id) || 0,
        }))
        .sort((a, b) => b.messageCount - a.messageCount)
        .slice(0, 10);

      // Calcular distribuição de planos
      const planCounts = new Map<string, number>();
      tenants.forEach(tenant => {
        planCounts.set(tenant.plan, (planCounts.get(tenant.plan) || 0) + 1);
      });

      const planDistribution: PlanDistribution[] = Array.from(planCounts.entries())
        .map(([plan, count]) => ({
          plan: plan.charAt(0).toUpperCase() + plan.slice(1),
          count,
          color: PLAN_COLORS[plan] || 'hsl(var(--muted))',
        }))
        .sort((a, b) => b.count - a.count);

      // v1.1.0: Extrair providers únicos dos dados da RPC (sem SELECT direto a api_providers)
      const providerMap = new Map<string, {
        name: string;
        slug: string;
        logoUrl: string | null;
        channelType: string;
        isActive: boolean;
      }>();

      adminInboxes.forEach(row => {
        if (row.provider_slug && !providerMap.has(row.provider_slug)) {
          providerMap.set(row.provider_slug, {
            name: row.provider_name || row.provider_slug,
            slug: row.provider_slug,
            logoUrl: row.provider_logo_url,
            channelType: row.provider_channel_type || row.inbox_channel_type,
            isActive: row.provider_is_active ?? true,
          });
        }
      });

      // Calcular saúde dos providers a partir dos dados da RPC
      const providerHealth: ProviderHealth[] = Array.from(providerMap.entries())
        .filter(([, p]) => p.isActive)
        .map(([slug, provider]) => {
          const providerInboxes = adminInboxes.filter(i => i.provider_slug === slug);
          const total = providerInboxes.length;
          const active = providerInboxes.filter(i => i.inbox_status === 'active').length;
          const connecting = providerInboxes.filter(i => i.inbox_status === 'connecting').length;
          const error = providerInboxes.filter(i => i.inbox_status === 'error').length;
          const disconnected = providerInboxes.filter(i => i.inbox_status === 'disconnected').length;
          const inactive = providerInboxes.filter(i => i.inbox_status === 'inactive').length;

          const healthScore = total > 0
            ? Math.round(((active + connecting * 0.5) / total) * 100)
            : 100;

          return {
            providerId: slug, // v1.1.0: Usa slug como identificador (UUID não exposto)
            providerName: provider.name,
            providerSlug: provider.slug,
            logoUrl: provider.logoUrl,
            channelType: provider.channelType,
            totalInboxes: total,
            activeInboxes: active,
            connectingInboxes: connecting,
            errorInboxes: error,
            disconnectedInboxes: disconnected,
            inactiveInboxes: inactive,
            healthScore,
          };
        })
        .filter((p: ProviderHealth) => p.totalInboxes > 0)
        .sort((a: ProviderHealth, b: ProviderHealth) => b.totalInboxes - a.totalInboxes);

      // Gerar alertas do sistema a partir dos dados da RPC
      const systemAlerts: SystemAlert[] = [];

      // Alertas para inboxes com erro
      adminInboxes
        .filter(i => i.inbox_status === 'error')
        .forEach(inbox => {
          systemAlerts.push({
            id: `error-${inbox.inbox_id}`,
            type: 'error',
            title: 'Inbox com Erro',
            description: `A inbox "${inbox.inbox_name}" está com status de erro e precisa de atenção.`,
            tenantName: inbox.tenant_name,
            inboxName: inbox.inbox_name,
            createdAt: new Date().toISOString(),
          });
        });

      // Alertas para inboxes desconectadas
      adminInboxes
        .filter(i => i.inbox_status === 'disconnected')
        .forEach(inbox => {
          systemAlerts.push({
            id: `disconnected-${inbox.inbox_id}`,
            type: 'warning',
            title: 'Inbox Desconectada',
            description: `A inbox "${inbox.inbox_name}" foi desconectada e precisa reconexão.`,
            tenantName: inbox.tenant_name,
            inboxName: inbox.inbox_name,
            createdAt: new Date().toISOString(),
          });
        });

      // Alertas para tenants suspensos
      tenants
        .filter(t => t.status === 'suspended')
        .forEach(tenant => {
          systemAlerts.push({
            id: `suspended-${tenant.id}`,
            type: 'warning',
            title: 'Cliente Suspenso',
            description: `O cliente "${tenant.name}" está suspenso.`,
            tenantName: tenant.name,
            createdAt: new Date().toISOString(),
          });
        });

      // Alerta informativo se não houver problemas
      if (systemAlerts.length === 0) {
        systemAlerts.push({
          id: 'all-good',
          type: 'info',
          title: 'Sistema Saudável',
          description: 'Todos os serviços estão funcionando normalmente.',
          createdAt: new Date().toISOString(),
        });
      }

      return {
        tenants: tenantStatsData,
        messages: messageStatsData,
        totalUsers: profilesResult.count || 0,
        totalInboxes: adminInboxes.length,
        topTenants,
        planDistribution,
        providerHealth,
        systemAlerts: systemAlerts.slice(0, 10),
      };
    },
    staleTime: 1000 * 60 * 5,
  });
}
